import { useEffect, useRef, useState } from "react";
import { useSuperwallEvents } from "@-/com.npmjs.expo-superwall";
import { Alert, View } from "@-/com.npmjs.react-native";
import {
  BottomSheet,
  type BottomSheetHandle,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { PremiumBenefitList } from "#premium-benefit-list";
import { useIsPremium } from "#use-is-premium";

/**
 * How long a "purchased" paywall result may wait for the entitlement to turn active. StoreKit can
 * report an old, already-expired transaction as a purchase (seen with a sandbox Apple Account that
 * had subscribed before): Superwall then closes the paywall as "purchased" while the subscription
 * status stays INACTIVE. Congratulating that buyer would claim an upgrade that never happened.
 */
const ENTITLEMENT_CONFIRMATION_DEADLINE_MS = 8000;

/**
 * Congratulates a buyer and lists what they unlocked, once the paywall that took the purchase
 * has finished dismissing (so the sheet never stacks under Superwall's modal).
 *
 * Mounted once in the app shell and listening to EVERY paywall's dismissal, rather than hooked
 * into `useUpgradeToPremium`'s placement callbacks: the component that opened the paywall is
 * usually a premium gate, which unmounts the moment the purchase flips `useIsPremium` — before
 * the paywall finishes closing — taking its `onDismiss` listener with it.
 */
export function PremiumWelcomeSheet() {
  const sheetRef = useRef<BottomSheetHandle>(null);
  const [isRequested, setIsRequested] = useState(false);
  const isPremium = useIsPremium();
  useSuperwallEvents({
    onPaywallDismiss(_paywallInfo, result) {
      if (result.type === "purchased") {
        setIsRequested(true);
      }
    },
  });
  // Open only once the purchase has really unlocked premium.
  useEffect(() => {
    if (isRequested && isPremium) {
      sheetRef.current?.open();
    }
  }, [isPremium, isRequested]);
  // A "purchase" whose entitlement never turns active gets the truth instead of a welcome.
  useEffect(() => {
    if (!isRequested || isPremium) {
      return;
    }
    const timeout = setTimeout(() => {
      setIsRequested(false);
      Alert.alert(
        "Subscription not confirmed",
        "The App Store didn't confirm an active Haniverse Plus subscription. If you were charged, open Settings > Account and tap Restore Purchases.",
      );
    }, ENTITLEMENT_CONFIRMATION_DEADLINE_MS);
    return () => clearTimeout(timeout);
  }, [isPremium, isRequested]);

  return (
    <BottomSheet
      footer={
        <Button onPress={() => sheetRef.current?.close()} testID="premium-welcome-sheet-dismiss">
          <Text>Start learning</Text>
        </Button>
      }
      onDismiss={() => setIsRequested(false)}
      ref={sheetRef}
      scrollable
    >
      <View className="gap-6 pb-4" testID="premium-welcome-sheet">
        <View className="items-center gap-2">
          <Icon className="text-foreground" name="LuTrophy" size={36} />
          <Text
            accessibilityRole="header"
            className="text-center font-sans text-[24px] font-semibold"
            colorVariant="default"
          >
            Welcome to Haniverse Plus!
          </Text>
          <Text className="text-center font-sans text-[15px]" colorVariant="muted">
            Thank you for subscribing to Haniverse. Your support keeps the app growing, and
            everything below is now unlocked.
          </Text>
        </View>
        <PremiumBenefitList />
      </View>
    </BottomSheet>
  );
}
